/** Resolve display name from row + attributes JSON (Excel often stores Name only in attributes). */

type NameRow = {
  full_name?: string | null;
  company_name?: string | null;
  email?: string | null;
  attributes?: Record<string, unknown> | null;
};

function attrString(attrs: Record<string, unknown>, key: string): string | null {
  const v = attrs[key];
  if (typeof v === 'string' && v.trim()) return v.trim();
  return null;
}

function normalizeNameToken(s: string): string {
  return s.trim().toLowerCase().replace(/[\s._+-]/g, '');
}

/** True when a value is a phone number stored in the name field (import mis-map). */
export function looksLikePhoneName(value: string | null | undefined): boolean {
  if (!value?.trim()) return false;
  const v = value.trim();
  const digits = v.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 13 && /^[\d\s\-+().]+$/.test(v);
}

/** True when stored name is just the email local-part (import bug / bad backfill). */
export function looksLikeEmailLocalName(name: string, email: string | null | undefined): boolean {
  if (!email?.includes('@')) return false;
  const local = email.split('@')[0] ?? '';
  if (!local.trim()) return false;
  return normalizeNameToken(name) === normalizeNameToken(local);
}

export function emailLocalDisplayName(email: string | null | undefined): string | null {
  if (!email?.includes('@')) return null;
  const local = email.split('@')[0]?.trim();
  return local || null;
}

function resolveNameFromAttributes(attrs: Record<string, unknown>): string | null {
  for (const key of ['Name', 'name', 'NAME', 'Contact person', 'Contact Person', 'Candidate Name']) {
    const v = attrString(attrs, key);
    if (v && !v.includes('@')) return v;
  }

  for (const [key, val] of Object.entries(attrs)) {
    if (typeof val !== 'string' || !val.trim() || val.includes('@')) continue;
    const nk = key.toLowerCase().replace(/\s+/g, ' ').trim();
    if (nk === 'name' || nk === 'contact person' || nk === 'contact_person') return val.trim();
    if (nk.endsWith(' name') && !nk.includes('customer') && !nk.includes('company') && !nk.includes('user')) {
      return val.trim();
    }
  }

  // Non-standard header keys (e.g. first data row used as header) — pick first human-looking value
  for (const val of Object.values(attrs)) {
    if (typeof val !== 'string') continue;
    const v = val.trim();
    if (!v || v.includes('@')) continue;
    if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(v)) continue;
    const digits = v.replace(/\D/g, '');
    if (digits.length >= 10 && digits.length <= 12 && /^\d[\d\s\-+()]+$/.test(v)) continue;
    if (/^\d+$/.test(v) && v.length <= 6) continue;
    if (v.length >= 2 && /[a-zA-Z]/.test(v)) return v;
  }

  return null;
}

export function getPersonDisplayName(row: NameRow): string {
  const fromAttrs =
    row.attributes && typeof row.attributes === 'object' && !Array.isArray(row.attributes)
      ? resolveNameFromAttributes(row.attributes as Record<string, unknown>)
      : null;

  const fromFull = row.full_name?.trim() || null;
  const fullIsBad =
    !fromFull
    || looksLikePhoneName(fromFull)
    || looksLikeEmailLocalName(fromFull, row.email);

  if (fromAttrs && fullIsBad) return fromAttrs;
  if (fromFull && !fullIsBad) return fromFull;
  if (fromAttrs) return fromAttrs;
  if (row.company_name?.trim()) return row.company_name.trim();

  const fromEmail = emailLocalDisplayName(row.email);
  if (fromEmail) return fromEmail;

  return '—';
}

/** First token for email greeting — never returns domain. */
export function getPersonFirstName(row: NameRow): string {
  const display = getPersonDisplayName(row);
  if (display === '—') return 'there';
  const first = display.split(/\s+/)[0]?.trim();
  return first || 'there';
}

export function getPersonLocation(row: {
  location?: string | null;
  attributes?: Record<string, unknown> | null;
}): string {
  if (row.location?.trim()) return row.location.trim();
  if (row.attributes && typeof row.attributes === 'object') {
    const attrs = row.attributes as Record<string, unknown>;
    for (const key of ['Current Location', 'Location', 'location', 'City', 'city']) {
      const v = attrString(attrs, key);
      if (v) return v;
    }
  }
  return '—';
}

export function getPersonFunctionalArea(row: {
  functional_area?: string | null;
  attributes?: Record<string, unknown> | null;
}): string {
  if (row.functional_area?.trim()) return row.functional_area.trim();
  if (row.attributes && typeof row.attributes === 'object') {
    const attrs = row.attributes as Record<string, unknown>;
    for (const key of ['Functional Area', 'functional area', 'Functional Area ']) {
      const v = attrString(attrs, key);
      if (v) return v;
    }
  }
  return '—';
}
